import Vue from "vue";
import VueI18n from "vue-i18n";
import Moment from "moment";
import deCH from "@/locales/de-CH";
import enGB from "@/locales/en-GB";
import frFR from "@/locales/fr-FR";
import itCH from "@/locales/it-CH";
// Vuetify's own translations for its built-in component strings (pagination, calendar, ...)
import vuetifyDe from "vuetify/lib/locale/de";
import vuetifyEn from "vuetify/lib/locale/en";
import vuetifyFr from "vuetify/lib/locale/fr";
import vuetifyIt from "vuetify/lib/locale/it";

Vue.use(VueI18n);

export const supportedLocales = ["de-CH", "fr-FR", "en-GB", "it-CH"] as const;
export type SupportedLocale = (typeof supportedLocales)[number];

const defaultLocale: SupportedLocale = "de-CH";
const storageKey = "locale";

// Locale used for date formatting (moment) and the Vuetify date picker.
const momentLocales: Record<SupportedLocale, string> = {
	"de-CH": "de-ch",
	"fr-FR": "fr",
	"en-GB": "en-gb",
	"it-CH": "it-ch",
};

// Language names are always shown in their own language, not translated -
// this is standard practice for language pickers.
export const localeDisplayNames: Record<SupportedLocale, string> = {
	"de-CH": "Deutsch",
	"fr-FR": "Français",
	"en-GB": "English",
	"it-CH": "Italiano",
};

export function isSupportedLocale(value: string | null | undefined): value is SupportedLocale {
	return !!value && (supportedLocales as readonly string[]).includes(value);
}

export function getStoredLocale(): SupportedLocale {
	const stored = localStorage.getItem(storageKey);
	return isSupportedLocale(stored) ? stored : defaultLocale;
}

/// Switches the active UI locale. Called once the user's persisted language preference
/// (Customer.User.language, see UserSettings.vue) is known, and whenever the user changes
/// it in the language selector. localStorage is only used to render the correct locale
/// on the next page load before that user info has been fetched again.
export function setLocale(locale: string) {
	if (!isSupportedLocale(locale)) {
		return;
	}
	i18n.locale = locale;
	localStorage.setItem(storageKey, locale);
	Moment.locale(momentLocales[locale]);
}

const i18n = new VueI18n({
	locale: getStoredLocale(),
	fallbackLocale: defaultLocale,
	messages: {
		"de-CH": { ...deCH, $vuetify: vuetifyDe },
		"fr-FR": { ...frFR, $vuetify: vuetifyFr },
		"en-GB": { ...enGB, $vuetify: vuetifyEn },
		"it-CH": { ...itCH, $vuetify: vuetifyIt },
	},
});

Moment.locale(momentLocales[i18n.locale as SupportedLocale]);

export default i18n;
